import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas, Tabs, Tab, Toast, Badge, Button } from "react-bootstrap";
import { ActionHandlerFunction, AdminApprovalGroup, AdminUser, OrgInfo } from "@preveil-api";
import { AdminUIActionTypes, PrivateRoutes, account_types, OrgUserStatus, Account, getAccountTooltip, OrgUserRoles } from "src/common";
import { Icon, InfoPopover } from "src/components";
import { AccountPanel, DevicePanel, ConvertToExpressModal } from ".";

type AllProps = {
  current_selected_user: AdminUser;
  total_admin_users: number;
  current_account: Account;
  recovery_groups: AdminApprovalGroup[];
  users_list: AdminUser[];
  allow_add_devices?: boolean;
  add_device_overrides: string[];
  admin_approval_group?: AdminApprovalGroup;
  org_info: OrgInfo;
  handleAction: ActionHandlerFunction;
};

function SidePanelComponent(props: AllProps) {
  const { total_admin_users, current_selected_user, current_account, recovery_groups, allow_add_devices, add_device_overrides, users_list,
    admin_approval_group, org_info, handleAction } = props;
  const { display_name, account_type, updates_pending, subsume_request_id, user_pending_delete_total } = current_selected_user;
  const updates_pending_message = !!user_pending_delete_total && user_pending_delete_total >= 1 ? "Delete approval pending" :
    updates_pending && updates_pending > 1 ? `${updates_pending} updates are awaiting approval` :
      `${updates_pending} update is awaiting approval`;
  const [show_convert_to_express_modal, setShowConvertToExpressModal] = useState<boolean>(false);
  const pending_conversion = current_selected_user.pending_account_type === account_types.express;
  
  // Description: handle hide modal.
  function handleHideModal() {
    handleAction({ actionType: AdminUIActionTypes.ShowSideNavMenu });
  }

  // Description: disable convert to express button if the user is already an express account, not an active user, an admin, or part of a recovery group.
  function shouldPreventConvertExpress(): boolean {
    return !(current_selected_user.account_type === account_types.express 
      || current_selected_user.status !== OrgUserStatus.active
      || current_selected_user.role === OrgUserRoles.admin 
      || recovery_groups?.find((recovery_group) => recovery_group.approvers.find((approver) => approver.address === current_selected_user.user_id))
    );
  }

  return (
    <>
      <ConvertToExpressModal 
        current_selected_user={current_selected_user}
        org_info={org_info}
        current_account={current_account}
        show={show_convert_to_express_modal} 
        setShowModal={setShowConvertToExpressModal} 
        handleAction={handleAction}
      />
      <Offcanvas
        className="sidebar wide-panel user-sidebar"
        show={true}
        placement="end"
        backdrop={false}
        onHide={handleHideModal}>
        <Offcanvas.Header closeButton>
          <Offcanvas.Title as="h2">
            <Icon tooltip={getAccountTooltip(account_type, current_selected_user.pending_account_type)} className={`${account_type === account_types.full ? current_selected_user.pending_account_type === account_types.express ? "icon-user inactive" : "icon-user-plus" : account_type === account_types.express ? "icon-user" : "icon-user-plus inactive"}`} />
            {display_name}
          </Offcanvas.Title>
          {
            (current_selected_user.status === OrgUserStatus.invited || current_selected_user.status === OrgUserStatus.pending || current_selected_user.pending_account_type === account_types.express) &&
            <>
              <Badge bg="info">{current_selected_user.pending_account_type === account_types.express ? OrgUserStatus.pending : current_selected_user.status}</Badge>
              {pending_conversion && <InfoPopover message={"The user needs to convert their account by logging into web.preveil.com"} placement="bottom-end" />}
            </>
          }
          {shouldPreventConvertExpress() &&
            <div className="btns">
              <Button variant="secondary" className="btn btn-secondary btn-sm" onClick={() => {setShowConvertToExpressModal(true);}}>
                {pending_conversion ? "Cancel Request" : "Convert to Express"}
              </Button>
            </div>
          }
        </Offcanvas.Header>
        
        <Offcanvas.Body>
          {
            !!updates_pending ?
              <Link to={`/${PrivateRoutes.admin_route}/${PrivateRoutes.requests_route}`}
                className="user-update">{updates_pending_message}</Link> :
              (current_selected_user.status === OrgUserStatus.invited &&
                <Link to={`/${PrivateRoutes.admin_route}/${PrivateRoutes.requests_route}/${subsume_request_id}`}>
                  <Toast bg="warning" show={true} className="mt-0 mb-3">
                    <Toast.Body>
                      This user has not yet accepted the invitation to join your organization.
                    </Toast.Body>
                  </Toast></Link>
              )
          }
          <Tabs defaultActiveKey="account" id="user-account">
            <Tab eventKey="account" title="Account" className="account-panel">
              <AccountPanel current_account={current_account} total_admin_users={total_admin_users} current_selected_user={current_selected_user} recovery_groups={recovery_groups} handleAction={handleAction} users_list={users_list} admin_approval_group={admin_approval_group}></AccountPanel>
            </Tab>
            <Tab eventKey="devices" title="Devices" className="device-panel">
              <DevicePanel current_user={current_selected_user} current_account={current_account} allow_add_devices={allow_add_devices} add_device_overrides={add_device_overrides} />
            </Tab>
          </Tabs>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}

export default React.memo(SidePanelComponent);
